import { useCallback, useEffect } from 'react';

import { CoreAuthProvider } from '@givebacks/api';
import { ShopProvider } from '@givebacks/shop';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CoreProvider, FeatureProvider_Core, InternalToolsMenu } from 'core';
import { ErrorBoundary } from 'core/Components';
import { useSetResizeListener } from 'core/Hooks';
import { GrowthBook, GrowthBookProvider } from 'core/Packages';
import { AppVersionController } from 'core/Utils';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { LoadingOverlay, notifications } from 'ui';

import { CarteraMnToolbarDataClient } from '../clients/CarteraMnToolbarDataClient';
import useMobileRouter from '../hooks/mobile/useMobileRouter';
import { growthBookClientKey } from '../utils/growthbook';
import './App.scss';
import { UserWatcher } from './Context/UserContext';
import { RoutesPaths } from './Routes';

declare global {
  interface Window {
    ReactRouterHistory: any;
    dataLayer: any[];
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1, // 1 minute
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const growthbook = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: growthBookClientKey(),
  enableDevMode: import.meta.env.VITE_API_ENV == 'production' ? false : true,
  trackingCallback: (experiment, result) => {
    console.log('Experiment Viewed', {
      experimentId: experiment.key,
      variationId: result.key,
    });
  },
});

// Provides native app utilities to the app
const NativeAppController = () => {
  const { push } = useMobileRouter();

  const webview = window.ReactNativeWebView;

  if (!webview) {
    return null;
  }

  const customHistory = { push };
  window.ReactRouterHistory = customHistory;
  return null;
};

const App = (): JSX.Element => {
  useSetResizeListener();

  const onRefreshTokenFailure = useCallback(() => {
    notifications.show({
      variant: 'error',
      message: 'Your session has expired. Please sign in again.',
      id: 'session-expired',
    });
  }, []);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    growthbook.loadFeatures();
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <QueryClientProvider client={queryClient}>
        <CoreAuthProvider
          showLoader={true}
          LoaderComponent={LoadingOverlay}
          onRefreshTokenFailure={onRefreshTokenFailure}
        >
          <CoreProvider
            errorBoundaryConfig={{
              ErrorBoundaryComponent: Sentry.ErrorBoundary,
            }}
          >
            <ErrorBoundary.Page>
              <FeatureProvider_Core>
                <BrowserRouter>
                  <ShopProvider
                    isConsolidatedCheckoutEnabled={true}
                    router={{
                      useNavigate: useNavigate,
                      useLocation: useLocation,
                    }}
                  >
                    <NativeAppController />
                    <RoutesPaths />
                    <UserWatcher />
                    <CarteraMnToolbarDataClient />
                  </ShopProvider>
                  <InternalToolsMenu />
                </BrowserRouter>
              </FeatureProvider_Core>
            </ErrorBoundary.Page>
          </CoreProvider>
        </CoreAuthProvider>
        <ReactQueryDevtools buttonPosition="bottom-left" />
      </QueryClientProvider>
      <AppVersionController />
    </GrowthBookProvider>
  );
};

export default App;
